import connect_icon from "@assets/icons/connect.svg";
import delete_icon from "@assets/redesign/icons/trash.svg";
import qr_code_icon from "@assets/icons/qr_code.svg";
import {
  ICustomEventDetail,
  SocketContext,
} from "@components/Context/SocketContext";
import RejectedChart from "@components/global/RejectedChart";
import Algorithm from "@components/global/new/Algorithm/Algorithm";
import Icon from "@components/global/new/Icon/Icon";
import Image from "@components/global/new/Image/Image";
import Status from "@components/global/new/Status/Status";
import { MINER_STATUSES_E } from "@constants/MinerStatuses";
import { isNewMiner } from "@services/addNewMiner";
import {
  MinerBaseEvent,
  MinerHashEvent,
  SocketEventTypes,
  SocketMessage,
} from "@services/http/socket/responses";
import { RootState } from "@store";
import { isMinerSelected } from "@store/minersSelect/actionTypes";
import { draggableHTMLID } from "@store/minersSelect/reducer";
import { tabIDToType } from "@store/minersSelect/actionTypes";
import { getDevivedHash } from "@utils/hash.utils";
import useMinerChangeControl from "@utils/hooks/useMinerChangeControl";
import useMediaQuery from "@utils/hooks/utils";
import { Checkbox, Collapse, Tooltip } from "antd";
import cn from "classnames";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import CopyItem from "../../../global/new/CopyItem/CopyItem";
import UserTags from "../../../global/new/UserTags/UserTags";
import ColoredChanges from "./ColoredChanges/ColoredChanges";
import { useMinerContext } from "./MinerContext";
import MinerHashSetting from "./MinerHashSetting/MinerHashSetting";
import MinerIdField from "./MinerIdField/MinerIdField";
import MinerLocation from "./MinerLocation/MinerLocation";
import styles from "./MinerRow.module.scss";
import { MinerStyledRowsProps } from "./MinerRow.props";
import MinerRowMore from "./MinerRowMore/MinerRowMore";
import MinerTypeCell from "./MinerTypeCell/MinerTypeCell";
import { MinerActions } from "./minerReducer";
import { onSocketEvent } from "./onSocketEvent";
import MinerFlag from "@components/global/new/MinerFlag/MinerFlag";
import { useMinerRowControl } from "./useMinerRowControl";
import { MenuOutlined, WarningOutlined } from "@ant-design/icons";
import {
  lastMinerActivity,
  useIsLastMinerActivity,
} from "./lastMinerActivity";
import { Panel } from "@components/global/new/Panel/Panel";
import chevronDown from "../../../../assets/redesign/icons/chevronDown.svg";
import chevronUp from "../../../../assets/redesign/icons/chevronUp.svg";
import ReproxyLink from "@components/global/ReproxyLink/ReproxyLink";
import { useSettings } from "@utils/hooks/useMe";
import { useMiningPlans } from "@utils/hooks/useMiningPlans";
import { WLP202603 } from "../../../../config";


const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

interface MinerIdFieldProps {
  title: string;
  text: string;
}

function MinerMetaField({
  title, text
}: MinerIdFieldProps): JSX.Element {
  return (
    <div>
      <strong>
        {title}
        :{" "}
      </strong>
      <span>{text}</span>
    </div>
  );
}

function MinerRow({ ...props }: MinerStyledRowsProps) {
  const { rowsStyles, cbStyle, selectMiner } = props;

  const [active, setActive] = useState("");

  const { addMessageListener, removeMessageListener } =
    useContext(SocketContext);

  const plans = useMiningPlans();

  const { formatMessage } = useIntl();
  const { miner, dispatchMiner } = useMinerContext();
  const isTablet = useMediaQuery("max", 1025);
  //const isDesktop = useMediaQuery("min", 1025);

  const control = useMinerRowControl();
  const isLastMinerActivity = useIsLastMinerActivity(miner.id);
  const changeCtrl = useMinerChangeControl(miner.id);

  const selectedMiners = useSelector((state: RootState) => state.minersSelect);
  const tab = useSelector((state: RootState) =>
    tabIDToType(state.minersSelect.tab)
  );

  function onMinerHashRate(message: SocketMessage<any>) {
    if (`${miner.id}` !== `${message.event.id}`) return;
    dispatchMiner({
      type: MinerActions.SpeedAndStatus,
      payload: message.event as MinerHashEvent,
    });
  }

  const onMinerEvent = useCallback((e: Event) => {
    const data = (e as CustomEvent).detail as ICustomEventDetail;
    if (data.type === SocketEventTypes.MINER_PLAN) {
      // add full plan
      const plan = plans?.find((p) => p.id === Number((data.event as any).plan_id)); // !!! нужно доработать типизацию сокетов
      if (plan) {
        onSocketEvent(data.type, plan, dispatchMiner);
        return;
      }
    }
    onSocketEvent(data.type, data.event, dispatchMiner);
  }, [plans]);

  useEffect(() => {
    const eventType = `miner-${miner.id}`;
    document.addEventListener(eventType, onMinerEvent);
    addMessageListener(SocketEventTypes.MINER_HASRATE, onMinerHashRate);
    return () => {
      document.removeEventListener(eventType, onMinerEvent);
      removeMessageListener(SocketEventTypes.MINER_HASRATE, onMinerHashRate);
    };
  }, [miner.id]);

  const onSelect = (e: React.MouseEvent<HTMLDivElement>) => {
    selectMiner(miner, e);
  };

  const incorrectID = control.miner.tab === "warn";

  const row = (
    <Panel
      theme={active ? "orange" : "light"}
      className={styles.head}
      onClick={() => setActive(active ? "" : "1")}
    >
      <div
        className={styles.drag_pad}
        onPointerDown={control.startDrag}
        onClick={stopPropagation}
      >
        <MenuOutlined
          className={styles.move_icon}
          style={{ color: "#0a28ab" }}
          rev={undefined}
        />
      </div>
      <div
        className={cn(styles.row, styles.after_statuses, {
          [styles.isAdmin]: control.isAdmin,
          [styles.connection_error]: control.isMinerError(),
          [styles.mining_admin]: miner.admin_mining_do,
          [styles.newMiner]: isNewMiner(miner),
        })}
        data-error-hint={formatMessage({ id: "machines.miner_row.error_hint" })}
      >
        <span onClick={stopPropagation} className={styles.checkbox}>
          <Checkbox
            checked={isMinerSelected(selectedMiners, miner.id.toString())}
            onClick={onSelect}
            style={{ ...cbStyle }}
          />
        </span>
        <div
          className={cn(styles.table_item, {
            [styles.incorrectID]: incorrectID,
          })}
          style={{ ...rowsStyles[0], wordWrap: "break-word" }}
          title={
            control.miner.unknown_miner_id
              ? formatMessage({ id: "machines.miner_row.unknown_id" })
              : undefined
          }
        >
          <div onClick={stopPropagation}>
            <MinerIdField
              minerId={miner.id}
              text={miner.miner_id}
              copyClassName={styles.opacityClass}
              className={styles.minerId}
            />
            {incorrectID && (
              <MinerIdField
                minerId={miner.id}
                text={miner.unknown_miner_id ?? ""}
                copyClassName={styles.opacityClass}
                className={styles.minerId}
                isDeviceID
              />
            )}
          </div>
        </div>
        <CopyItem
          className={styles.table_item}
          style={{
            ...rowsStyles[1],
          }}
          copyClassName={styles.opacityClass}
          text={miner.profile?.name || ""}
        >
          <ColoredChanges
            style={{
              maxWidth: "calc(100% - 20px)",
              wordWrap: "break-word",
            }}
            className={styles.value}
          >
            {miner.profile?.name}
          </ColoredChanges>
        </CopyItem>
        <div className={styles.table_item} style={{ ...rowsStyles[2] }}>
          <Algorithm
            algo={miner.profile.hash || miner.hash}
            string
            className={styles.algorithm}
          />
        </div>
        <MinerTypeCell
          className={styles.table_item}
          style={{ ...rowsStyles[3] }}
          copyClassName={styles.opacityClass}
        />
        <CopyItem
          className={styles.table_item}
          style={{ ...rowsStyles[4] }}
          copyClassName={styles.opacityClass}
          text={getDevivedHash(miner.speed_current, { unit: miner.hash })}
        >
          <ColoredChanges>
            {getDevivedHash(miner.speed_current, { unit: miner.hash })}
          </ColoredChanges>
        </CopyItem>
        <CopyItem
          className={styles.table_item}
          style={{ ...rowsStyles[5] }}
          copyClassName={styles.opacityClass}
          text={miner.setting}
        >
          {miner.setting ? (
            <div onClick={stopPropagation}
              className={cn({
                [styles.usedUserSetting]: !control.miner.is_own_mining,
              })}>
              <MinerHashSetting settingid={miner.settingid}>
                {miner.setting}
              </MinerHashSetting>
            </div>
          ) : (
            <span style={{ color: "#ff5151", textTransform: "uppercase" }}>
              [{formatMessage({ id: "algo.error" })}]
            </span>
          )}
        </CopyItem>
        <div
          className={styles.table_item}
          style={{
            ...rowsStyles[7],
            display: "flex",
            gap: 10,
          }}
        >
          <Status
            status={miner.status}
            admin_mining={miner.admin_mining_do}
            speed={miner.speed_current}
          />
          {(miner.status === MINER_STATUSES_E.BAD ||
            miner.status === MINER_STATUSES_E.LOW ||
            miner.status === MINER_STATUSES_E.OK) && (
              <RejectedChart
                rejected={miner.rejected}
                accepted={miner.accepted}
              />
            )}
          {/* {miner.has_errors && (
            <WarningOutlined twoToneColor="#ff0000" />
          )} */}
        </div>
        <div className={styles.table_item} style={{ ...rowsStyles[7] }}>
          <MinerLocation className={styles.location}>
            {miner.location_raw?.name ||
              miner.location ||
              formatMessage({ id: "machines.table.locations.empty" })}
          </MinerLocation>
        </div>
        <div
          className={cn(styles.table_item, styles.customer)}
          style={{ ...rowsStyles[8] }}
        >
          <div className={styles.icons} onClick={stopPropagation}>
            {control.isAdmin && (
              <>
                <ReproxyLink
                  minerId={miner.id}
                  link={miner.reproxy_link}
                  onDoPortClicked={control.createPortAndOpen}
                  onDeletePortClicked={control.deleteMinerPort}
                />
                <Tooltip
                  style={{ maxWidth: "initial" }}
                  title={
                    <div className={styles.qr}>
                      <h5>
                        <span>
                          {formatMessage({ id: "machines.hint.minerId" })}:{" "}
                        </span>
                        <span>{miner.miner_id}</span>
                      </h5>
                      <Image src={control.minerQrImage} />
                      <div>
                        {miner.meta?.ip && <MinerMetaField
                          title={formatMessage({ id: "machines.more.settings.ip" })}
                          text={miner.meta?.ip} />}
                        {miner.meta?.mac && <MinerMetaField
                          title={formatMessage({ id: "machines.more.settings.mac" })}
                          text={miner.meta?.mac} />}
                        {miner.meta?.sn && <MinerMetaField
                          title={formatMessage({ id: "machines.more.serial.title" })}
                          text={miner.meta?.sn} />}
                      </div>
                    </div>
                  }
                  open={control.showQr}
                  color="#fff"
                  placement="top"
                >
                  <Icon
                    src={qr_code_icon}
                    title={formatMessage({ id: "machines.hint.miner_qr" })}
                    loading={control.qrIsLoading}
                    onClick={() => control.loadImage(miner.id)}
                  />
                </Tooltip>
                <Icon
                  src={connect_icon}
                  onClick={control.onConnectHandler}
                  title={formatMessage({ id: "machines.hint.connect" })}
                  loading={control.isLoading}
                />
              </>
            )}
            <div className={styles.flag}>
              <MinerFlag
                optionalClassName={styles.opacityClass}
                minerId={control.miner.id}
                meta={control.miner.meta}
              />
            </div>

            {control.isAdmin &&
              miner.status === MINER_STATUSES_E.OFF && tab !== "unknown" && (
                <Icon
                  src={delete_icon}
                  onClick={control.onDeleteHandler}
                  title={formatMessage({ id: "machines.hint.delete" })}
                />
              )}
          </div>
          {!!miner.tags && (
            <UserTags
              tags={miner.tags}
              color={miner.user_color}
              className={styles.tag}
            />
          )}
          <ColoredChanges className={styles.customerEmail}>
            {miner.customer_email}
          </ColoredChanges>
        </div>
        <img src={active ? chevronUp : chevronDown} />
      </div>
    </Panel>
  );

  return (
    <div
      className={cn(styles.row_wrapper, {
        [styles.change]: changeCtrl.changed,
        [styles.noDrag]: !control.isDrag,
        [styles.dragOver]: control.isDragOver,
        [styles.noDrop]: control.noDrop,
        [styles.lastMinerActivity]: isLastMinerActivity,
      })}
      onMouseDownCapture={() => lastMinerActivity.set(miner.id)}
      onClick={control.moreModal}
      id={draggableHTMLID(`${control.miner.id}`)}
      onMouseLeave={() => {
        control.setShowQr(false);
        control.onMouseLeave();
      }}
      onMouseEnter={control.onMouseEnter}
      onMouseUp={control.onMouseUpOver}
    >
      <Collapse
        className={styles.collapse}
        collapsible="icon"
        ghost
        activeKey={active}
        items={[
          {
            key: "1",
            label: row,
            children: !isTablet && (
              <Panel theme="dark" className={styles.more}>
                <MinerRowMore
                  className={cn(styles.row_more)}
                  stateChanged={(state) => setActive(state ? "1" : "")}
                  isOpen={Boolean(active)}
                />
              </Panel>
            ),
            showArrow: false,
            forceRender: true,
          },
        ]}
      />
    </div>
  );
}

export default MinerRow;
